/**
 * Centralized Status Configuration
 *
 * Single source of truth for all status labels and colors across the admin portal.
 * Eliminates duplication and ensures consistency.
 */

export interface StatusConfig {
  label: string
  labelSr: string
  color: string
  description?: string
}

// ==================== APPOINTMENT STATUSES ====================

export const APPOINTMENT_STATUS: Record<string, StatusConfig> = {
  SCHEDULED: {
    label: 'Scheduled',
    labelSr: 'Zakazano',
    color: 'blue',
    description: 'Appointment is scheduled',
  },
  CONFIRMED: {
    label: 'Confirmed',
    labelSr: 'Potvrđeno',
    color: 'cyan',
    description: 'Patient confirmed attendance',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    labelSr: 'U toku',
    color: 'orange',
    description: 'Consultation is ongoing',
  },
  COMPLETED: {
    label: 'Completed',
    labelSr: 'Završeno',
    color: 'green',
    description: 'Consultation completed',
  },
  CANCELLED: {
    label: 'Cancelled',
    labelSr: 'Otkazano',
    color: 'default',
    description: 'Appointment was cancelled',
  },
  NO_SHOW: {
    label: 'No Show',
    labelSr: 'Propušteno',
    color: 'red',
    description: 'Patient did not attend',
  },
}

// ==================== LAB RESULT STATUSES ====================

export const LAB_RESULT_STATUS: Record<string, StatusConfig> = {
  PENDING: {
    label: 'Pending',
    labelSr: 'Na čekanju',
    color: 'default',
    description: 'Awaiting lab results',
  },
  RECEIVED: {
    label: 'Received',
    labelSr: 'Primljeno',
    color: 'blue',
    description: 'Results received from lab',
  },
  AWAITING_REVIEW: {
    label: 'Awaiting Review',
    labelSr: 'Čeka pregled',
    color: 'orange',
    description: 'Needs doctor review',
  },
  APPROVED: {
    label: 'Approved',
    labelSr: 'Odobreno',
    color: 'green',
    description: 'Results reviewed and approved',
  },
  REJECTED: {
    label: 'Rejected',
    labelSr: 'Odbijeno',
    color: 'red',
    description: 'Results rejected or need retesting',
  },
  SHARED: {
    label: 'Shared',
    labelSr: 'Deljeno',
    color: 'purple',
    description: 'Results shared with patient',
  },
}

// ==================== USER STATUSES ====================

export const USER_STATUS: Record<string, StatusConfig> = {
  ACTIVE: {
    label: 'Active',
    labelSr: 'Aktivan',
    color: 'success',
    description: 'User is active',
  },
  SUSPENDED: {
    label: 'Suspended',
    labelSr: 'Suspendovan',
    color: 'warning',
    description: 'Account temporarily suspended',
  },
  DELETED: {
    label: 'Deleted',
    labelSr: 'Obrisan',
    color: 'default',
    description: 'Account deleted',
  },
}

// ==================== WORKFLOW INSTANCE STATUSES ====================

export const WORKFLOW_INSTANCE_STATUS: Record<string, StatusConfig> = {
  ACTIVE: {
    label: 'Active',
    labelSr: 'Aktivan',
    color: 'success',
    description: 'Workflow in progress',
  },
  COMPLETED: {
    label: 'Completed',
    labelSr: 'Završen',
    color: 'green',
    description: 'Workflow completed successfully',
  },
  CANCELLED: {
    label: 'Cancelled',
    labelSr: 'Otkazan',
    color: 'default',
    description: 'Workflow cancelled',
  },
  ON_HOLD: {
    label: 'On Hold',
    labelSr: 'Na čekanju',
    color: 'orange',
    description: 'Workflow paused',
  },
}

// ==================== WORKFLOW PARENT STAGES ====================

export const WORKFLOW_PARENT_STAGE: Record<string, { color: string; icon: string; label: string; labelSr: string }> = {
  QUIZ: {
    color: 'blue',
    icon: 'FileTextOutlined',
    label: 'Questionnaire',
    labelSr: 'Upitnik',
  },
  LAB_TEST: {
    color: 'purple',
    icon: 'ExperimentOutlined',
    label: 'Lab Test',
    labelSr: 'Laboratorija',
  },
  CONSULTATION: {
    color: 'cyan',
    icon: 'VideoCameraOutlined',
    label: 'Consultation',
    labelSr: 'Konsultacija',
  },
  PRESCRIPTION: {
    color: 'orange',
    icon: 'MedicineBoxOutlined',
    label: 'Prescription',
    labelSr: 'Recept',
  },
  TREATMENT: {
    color: 'green',
    icon: 'HeartOutlined',
    label: 'Treatment',
    labelSr: 'Terapija',
  },
}

// ==================== APPOINTMENT TYPES ====================

export const APPOINTMENT_TYPE: Record<string, StatusConfig> = {
  INITIAL_CONSULTATION: {
    label: 'Initial Consultation',
    labelSr: 'Prva konsultacija',
    color: 'blue',
  },
  FOLLOW_UP: {
    label: 'Follow-up',
    labelSr: 'Kontrola',
    color: 'cyan',
  },
  RESULTS_REVIEW: {
    label: 'Results Review',
    labelSr: 'Pregled rezultata',
    color: 'purple',
  },
  PRESCRIPTION_RENEWAL: {
    label: 'Prescription Renewal',
    labelSr: 'Obnova recepta',
    color: 'orange',
  },
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Get status configuration with fallback
 */
export function getStatusConfig(
  statusMap: Record<string, StatusConfig>,
  status: string,
  fallbackLabel: string = status
): StatusConfig {
  return (
    statusMap[status] || {
      label: fallbackLabel,
      labelSr: fallbackLabel,
      color: 'default',
    }
  )
}

/**
 * Get status label (Serbian by default)
 */
export function getStatusLabel(statusMap: Record<string, StatusConfig>, status: string, locale: 'en' | 'sr' = 'sr'): string {
  const config = getStatusConfig(statusMap, status)
  return locale === 'sr' ? config.labelSr : config.label
}

/**
 * Get status color
 */
export function getStatusColor(statusMap: Record<string, StatusConfig>, status: string): string {
  return getStatusConfig(statusMap, status).color
}
